import { Link } from 'react-router-dom';
import { Button } from '@/components/common';
import { usePermission } from '@/hooks/usePermission';
import {
  errorMessage,
  errorStatus,
  useScreeningForTransaction,
} from '@/features/transaction-monitoring/api';
import { ScreeningBadge } from '@/features/transaction-monitoring/shared';
import { label } from '@/features/transaction-monitoring/types';

const box = 'space-y-3 rounded-lg border border-[#eee] bg-[#fafafa] p-5 text-sm text-[#666]';

/** Resolves this payment's own screening by its CBS transaction_id (exact server lookup). */
export function CompliancePanelTab({ transactionId }: { transactionId: string }) {
  const { has, hasRole } = usePermission();
  const allowed = hasRole('cbs_admin') && has('COMPLIANCE_VIEW');
  const query = useScreeningForTransaction('outbound', transactionId);
  if (!allowed)
    return (
      <div className={box}>
        <p>You do not have permission to view compliance screenings (COMPLIANCE_VIEW).</p>
      </div>
    );
  if (!/^[A-Za-z0-9][A-Za-z0-9-]{0,99}$/.test(transactionId))
    return (
      <div className={box}>
        <p className="font-semibold text-[#333]">Screening cannot be resolved</p>
        <p>
          This payment has no CBS transaction reference yet, so its compliance screening cannot be
          identified. No screening outcome can be inferred from this view.
        </p>
      </div>
    );
  if (query.isPending)
    return (
      <div className={box}>
        <p role="status">Looking up this payment’s compliance screening…</p>
      </div>
    );
  if (query.isError) {
    const status = errorStatus(query.error);
    return (
      <div role="alert" className={`${box} border-[#ffcdd2] bg-[#ffebee] text-[#c62828]`}>
        <p className="font-semibold">
          {status === 403
            ? 'You do not have permission to view this screening.'
            : status === 503
              ? 'Compliance screening is unavailable.'
              : 'The compliance screening lookup failed.'}
        </p>
        <p>{errorMessage(query.error)}</p>
        <p>This is not a screening result. The payment may or may not have been screened.</p>
        {status !== 403 && (
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            Retry lookup
          </Button>
        )}
      </div>
    );
  }
  const screening = query.data.screening;
  if (!screening)
    return (
      <div className={box}>
        <p className="font-semibold text-[#333]">Not screened yet</p>
        <p>
          No compliance screening is recorded for this payment. This is not a clear result; the
          screening is requested when the payment reaches pending.
        </p>
      </div>
    );
  return (
    <div className={box}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-[#333]">Screening decision</span>
        <ScreeningBadge status={screening.status} />
        {screening.hold_reason && (
          <span className="text-[#784600]">Hold reason: {label(screening.hold_reason)}</span>
        )}
      </div>
      <dl className="grid gap-2 sm:grid-cols-2">
        <div>
          <dt className="text-xs text-[#777]">Decision source</dt>
          <dd className="text-[#333]">{label(screening.decision_source)}</dd>
        </div>
        <div>
          <dt className="text-xs text-[#777]">Enforcement mode</dt>
          <dd className="text-[#333]">{label(screening.enforcement_mode)}</dd>
        </div>
      </dl>
      <p className="text-xs text-[#777]">
        The screening decision is separate from this payment’s financial status. In shadow mode an
        approved screening does not execute or release the payment.
      </p>
      <Link
        to={`/transaction-monitoring/${screening.screening_id}`}
        className="inline-block font-semibold text-[#6B0F1A] underline"
      >
        View compliance screening
      </Link>
    </div>
  );
}
